import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    FormControlLabel,
    Radio
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, FileDownload, FilterList, Close, PictureAsPdf, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api, { getBarterInfluencers } from '../services/api';
import { generateInfluencerPDF } from '../utils/pdfGenerator';

function BarterList() {
    const navigate = useNavigate();
    const [barterInfluencers, setBarterInfluencers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
    const [filters, setFilters] = useState({
        state: '',
        city: '',
        category: '',
        platformType: '',
        socialMediaPlatforms: '',
        influencerType: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [editModal, setEditModal] = useState({ open: false, id: null, platformType: '' });
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkModal, setBulkModal] = useState({ open: false, platformType: '' });

    const columns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'influencerCode', headerName: 'Influencer ID', width: 100 },
        { field: 'onboarderName', headerName: 'Onboarder Name', width: 150 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'email', headerName: 'Email', width: 200 },
        { field: 'contactNumber', headerName: 'Contact', width: 130 },
        { field: 'state', headerName: 'State', width: 120 },
        { field: 'city', headerName: 'City', width: 120 },
        { field: 'category', headerName: 'Category', width: 120 },
        { field: 'instagramInfluencerType', headerName: 'IG Type', width: 100 },
        { field: 'youtubeInfluencerType', headerName: 'YT Type', width: 100 },
        { field: 'platformType', headerName: 'Platform Type', width: 120 },
        { field: 'instagramLink', headerName: 'Instagram', width: 200 },
        { field: 'youtubeLink', headerName: 'YouTube', width: 200 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/influencer/${params.row._id}`)}
                        sx={{ color: '#019383' }}
                    >
                        <Visibility />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setEditModal({ open: true, id: params.row._id, platformType: params.row.platformType || '' })}
                        sx={{ color: '#019383' }}
                    >
                        <Edit />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => generatePDF(params.row)}
                        sx={{ color: '#019383' }}
                    >
                        <PictureAsPdf />
                    </IconButton>
                </Box>
            )
        }
    ];

    useEffect(() => {
        fetchBarterInfluencers();
    }, []);

    const fetchBarterInfluencers = async () => {
        setLoading(true);
        try {
            const response = await getBarterInfluencers();
            setBarterInfluencers(response.data?.data || []);
        } catch (error) {
            console.error('Fetch error:', error);
            showAlert('Failed to fetch barter influencers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showAlert = (message, severity) => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 3000);
    };

    const handleUpdatePlatformType = async () => {
        try {
            await api.patch(`/influencers/${editModal.id}/platform-type`, { platformType: editModal.platformType });
            showAlert('Platform type updated successfully', 'success');
            setEditModal({ open: false, id: null, platformType: '' });
            fetchBarterInfluencers();
        } catch (error) {
            console.error('Update error:', error);
            showAlert('Failed to update platform type', 'error');
        }
    };

    const handleBulkUpdatePlatformType = async () => {
        try {
            await api.patch('/influencers/bulk/platform-type', { ids: selectedRows, platformType: bulkModal.platformType });
            showAlert(`${selectedRows.length} influencer(s) updated to ${bulkModal.platformType}`, 'success');
            setBulkModal({ open: false, platformType: '' });
            setSelectedRows([]);
            fetchBarterInfluencers();
        } catch (error) {
            console.error('Bulk update error:', error);
            showAlert('Failed to bulk update platform type', 'error');
        }
    };

    const generatePDF = (influencer) => {
        generateInfluencerPDF(influencer, 'barter_details');
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const getFilteredRows = () => {
        let filtered = barterInfluencers;

        if (filters.state) {
            filtered = filtered.filter(inf => inf.state === filters.state);
        }
        if (filters.city) {
            filtered = filtered.filter(inf => inf.city === filters.city);
        }
        if (filters.category) {
            filtered = filtered.filter(inf => inf.category === filters.category);
        }
        if (filters.platformType) {
            filtered = filtered.filter(inf => inf.platformType === filters.platformType);
        }
        if (filters.socialMediaPlatforms) {
            if (filters.socialMediaPlatforms === 'Barter') {
                filtered = filtered.filter(inf => inf.platformType === 'Barter');
            } else {
                filtered = filtered.filter(inf =>
                    inf.socialMediaPlatforms &&
                    inf.socialMediaPlatforms.includes(filters.socialMediaPlatforms)
                );
            }
        }
        if (filters.influencerType) {
            filtered = filtered.filter(inf =>
                inf.instagramInfluencerType === filters.influencerType ||
                inf.youtubeInfluencerType === filters.influencerType
            );
        }

        return filtered.map((inf, index) => ({
            ...inf,
            id: inf._id,
            _id: inf._id,
            sno: index + 1,
            influencerCode: inf.influencerCode || '-',
            onboarderName: inf?.onboarderId?.name || 'N/A',
            instagramLink: inf.instagramLink || '-',
            youtubeLink: inf.youtubeLink || '-',
            state: inf.state || '-',
            city: inf.city || '-',
            instagramInfluencerType: inf.instagramInfluencerType || '-',
            youtubeInfluencerType: inf.youtubeInfluencerType || '-'
        }));
    };

    const getUniqueValues = (field) => {
        return [...new Set(barterInfluencers.map(inf => inf[field]).filter(Boolean))];
    };

    const exportToExcel = () => {
        const filteredData = getFilteredRows();
        const exportData = filteredData.map(row => ({
            'S.No': row.sno,
            'Name': row.name,
            'Email': row.email,
            'Date of Birth': row.dateOfBirth || '-',
            'Age': row.age || '-',
            'Gender': row.gender || '-',
            'Language': row.language || '-',
            'Category': row.category || '-',
            'Brand Collaboration': row.brandCollaboration || '-',
            'Contact Number': row.contactNumber || '-',
            'Address': row.address || '-',
            'Pincode': row.pincode || '-',
            'State': row.state,
            'City': row.city,
            'Location': row.location || '-',
            'Platform Type': row.platformType || '-',
            'Social Media Platforms': Array.isArray(row.socialMediaPlatforms) ? row.socialMediaPlatforms.join(', ') : (row.socialMediaPlatforms || '-'),
            'Instagram Link': row.instagramLink,
            'YouTube Link': row.youtubeLink,
            'Instagram Followers': row.instagramFollowers || '-',
            'Instagram Type': row.instagramInfluencerType,
            'Instagram Likes': row.instagramLikes || '-',
            'Instagram Comments': row.instagramComments || '-',
            'Instagram Saves': row.instagramSaves || '-',
            'Instagram Shares': row.instagramShares || '-',
            'Instagram Reposts': row.instagramReposts || '-',
            'Last 10 Reels Views': row.last10ReelsViews || '-',
            'Last 10 Reels Reach': row.last10ReelsReach || '-',
            'Reel Cost': row.reelCost || '-',
            'Story Cost': row.storyCost || '-',
            'YouTube Followers': row.youtubeFollowers || '-',
            'YouTube Type': row.youtubeInfluencerType,
            'YouTube Likes': row.youtubeLikes || '-',
            'YouTube Comments': row.youtubeComments || '-',
            'YouTube Saves': row.youtubeSaves || '-',
            'YouTube Shares': row.youtubeShares || '-',
            'Last 10 Shorts Views': row.last10ShortsViews || '-',
            'Last 10 Shorts Reach': row.last10ShortsReach || '-',
            'YouTube Video Cost': row.youtubeVideoCost || '-',
            'YouTube Shorts Cost': row.youtubeShortsCost || '-',
            'PAN Number': row.panNumber || '-',
            'Bank Account Number': row.bankAccountNumber || '-',
            'IFSC Code': row.ifscCode || '-',
            'Account Holder Name': row.holderName || '-',
            'Bank Name': row.bankName || '-',
            'Delivery Address Same': row.deliveryAddressSame || '-',
            'Delivery Address': row.deliveryAddress || '-',
            'Remarks': row.remarks || '-',
            'Status': row.status || '-',
            'Created At': row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-',
            'Updated At': row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '-'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Barter Influencers');
        XLSX.writeFile(wb, `barter-influencers-${new Date().toISOString().split('T')[0]}.xlsx`);
        showAlert('Excel file exported successfully', 'success');
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Barter Influencers
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {selectedRows.length > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<Edit />}
                            onClick={() => setBulkModal({ open: true, platformType: '' })}
                        >
                            Bulk Update ({selectedRows.length})
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<FilterList />}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        Filters
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<FileDownload />}
                        onClick={exportToExcel}
                    >
                        Export Excel
                    </Button>
                </Box>
            </Box>

            {alert.show && (
                <Alert severity={alert.severity} sx={{ mb: 2 }}>
                    {alert.message}
                </Alert>
            )}

            {showFilters && (
                <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Filters</Typography>
                        <IconButton 
                            onClick={() => setShowFilters(false)}
                            sx={{ color: '#019383' }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>State</InputLabel>
                                <Select
                                    value={filters.state}
                                    label="State"
                                    onChange={(e) => handleFilterChange('state', e.target.value)}
                                >
                                    <MenuItem value="">All States</MenuItem>
                                    {getUniqueValues('state').map(state => (
                                        <MenuItem key={state} value={state}>{state}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>City</InputLabel>
                                <Select
                                    value={filters.city}
                                    label="City"
                                    onChange={(e) => handleFilterChange('city', e.target.value)}
                                >
                                    <MenuItem value="">All Cities</MenuItem>
                                    {getUniqueValues('city').map(city => (
                                        <MenuItem key={city} value={city}>{city}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={filters.category}
                                    label="Category"
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                >
                                    <MenuItem value="">All Categories</MenuItem>
                                    {getUniqueValues('category').map(category => (
                                        <MenuItem key={category} value={category}>{category}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                select
                                label="Platform Type"
                                value={filters.socialMediaPlatforms}
                                onChange={(e) => handleFilterChange('socialMediaPlatforms', e.target.value)}
                                size="small"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="instagram">Instagram</MenuItem>
                                <MenuItem value="youtube">Youtube</MenuItem>
                                <MenuItem value="Barter">Barter</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Influencer Type</InputLabel>
                                <Select
                                    value={filters.influencerType}
                                    label="Influencer Type"
                                    onChange={(e) => handleFilterChange('influencerType', e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="Micro">Micro: 10K-100K</MenuItem>
                                    <MenuItem value="Macro">Macro: 100K-500K</MenuItem>
                                    <MenuItem value="Mid-tier">Mid-tier: 500K-1M</MenuItem>
                                    <MenuItem value="Mega">Mega: 1M-5M</MenuItem>
                                    <MenuItem value="Celeb">Celeb: 5M+</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
                </Card>
            )}

            <Card>
                <CardContent>
                    <DataGrid
                        rows={getFilteredRows()}
                        columns={columns}
                        pageSize={10}
                        loading={loading}
                        autoHeight
                        checkboxSelection
                        onSelectionModelChange={(ids) => setSelectedRows(ids)}
                        selectionModel={selectedRows}
                        getRowId={(row) => row.id}
                    />
                </CardContent>
            </Card>
            <Dialog
                open={editModal.open}
                onClose={() => setEditModal({ open: false, id: null, platformType: '' })}
                PaperProps={{ sx: { minWidth: 400, minHeight: 250, borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 600, pb: 1 }}>Edit Platform Type</DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 3, px: 4 }}>
                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>Select the platform type for this influencer:</Typography>
                    <RadioGroup
                        value={editModal.platformType}
                        onChange={(e) => setEditModal(prev => ({ ...prev, platformType: e.target.value }))}
                    >
                        <FormControlLabel value="Paid" control={<Radio sx={{ '&.Mui-checked': { color: '#019383' } }} />} label="Paid" sx={{ mb: 1 }} />
                        <FormControlLabel value="Barter" control={<Radio sx={{ '&.Mui-checked': { color: '#019383' } }} />} label="Barter" />
                    </RadioGroup>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditModal({ open: false, id: null, platformType: '' })}>Cancel</Button>
                    <Button onClick={handleUpdatePlatformType} >Update</Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={bulkModal.open}
                onClose={() => setBulkModal({ open: false, platformType: '' })}
                PaperProps={{ sx: { minWidth: 400, minHeight: 250, borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 600, pb: 1 }}>Bulk Update Platform Type</DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 3, px: 4 }}>
                    <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>Update platform type for {selectedRows.length} selected influencer(s):</Typography>
                    <RadioGroup
                        value={bulkModal.platformType}
                        onChange={(e) => setBulkModal(prev => ({ ...prev, platformType: e.target.value }))}
                    >
                        <FormControlLabel value="Paid" control={<Radio sx={{ '&.Mui-checked': { color: '#019383' } }} />} label="Paid" sx={{ mb: 1 }} />
                        <FormControlLabel value="Barter" control={<Radio sx={{ '&.Mui-checked': { color: '#019383' } }} />} label="Barter" />
                    </RadioGroup>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setBulkModal({ open: false, platformType: '' })}>Cancel</Button>
                    <Button variant="contained" onClick={handleBulkUpdatePlatformType} disabled={!bulkModal.platformType}>Update All</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default BarterList;